import _ from 'lodash';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';
import { ComponentPropsWithRef, PropsWithChildren } from 'react';

import { IconButton } from '@treams/treams-ui';
import { ReplyIcon } from '@treams/treams-icons';

import * as graphql from 'generated/graphql';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';

const Bar = styled(Flex)`
	border-radius: 20px;
	background: ${(props) => props.theme.colors.white};
	box-shadow:
		0px 2px 4px ${(props) => rgba(props.theme.colors.slateBlue, 0.1)},
		0px 1px 2px ${(props) => rgba(props.theme.colors.slateBlue, 0.05)},
		0px 0px 0px 1px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)} inset;
`;

const Container = styled(Box)`
	position: relative;
	padding-bottom: 20px;
`;

const Holder = styled(Box)`
	position: absolute;
	bottom: 4px;
	right: -4px;
`;

const Emoji = styled(Flex)`
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	font-size: 20px;
`;

function EmojiButton(props: {
	targetID: string;
	targetType: graphql.CommentTargetType;
	metadata?: Record<string, string>;
	emoji: string;
}) {
	const [createComment] = graphql.useCreateCommentMutation({
		variables: {
			input: {
				targetID: props.targetID,
				targetType: props.targetType,
				metadata: _.entries(props.metadata).map(([key, value]) => ({ key, value })),
				content: { ops: [{ insert: props.emoji }] },
			},
		},
	});

	return (
		<IconButton
			icon={<Emoji>{props.emoji}</Emoji>}
			type="plain"
			onClick={async () => {
				await createComment();
			}}
		/>
	);
}

export default function CommentBar(props: {
	targetID: string;
	targetType: graphql.CommentTargetType;
	metadata?: Record<string, string>;
	onComment: () => void;
}) {
	return (
		<Bar p={1}>
			{['👍', '😃', '🙂', '🙏'].map((emoji, index) => (
				<EmojiButton
					key={index}
					targetID={props.targetID}
					targetType={props.targetType}
					metadata={props.metadata}
					emoji={emoji}
				/>
			))}
			<IconButton icon={<ReplyIcon />} type="plain" onClick={() => props.onComment()} />
		</Bar>
	);
}

export function CommentBarContainer(
	props: PropsWithChildren<ComponentPropsWithRef<typeof CommentBar>> & {
		enabled?: boolean;
	},
) {
	const useFeedbackReply = useFeature(graphql.FeatureName.FeedbackReply);

	return (
		<Container>
			{props.children}
			{props.enabled !== false && useFeedbackReply && (
				<Holder>
					<CommentBar {...props} />
				</Holder>
			)}
		</Container>
	);
}

CommentBar.Container = CommentBarContainer;
